import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { debounce, isEqual } from 'lodash';
import { isMobile } from 'helpers/mobile_helpers';

import ClusteredMap from 'components/maps/clustered_map';
import DrawingManager from './properties_map_drawing_manager';
import MapInfoBubble from 'bundles/research/index/components/map_info_bubble';

import { connect } from 'react-redux';
import {
  debouncedEnableAutoBoundsSearching,
  disableAutoBoundsSearching,
  enableAutoBoundsSearching,
  setGoogleMapsBounds,
  setUserBounds,
} from '../store/actions/coordinates';
import { goToPropertyShow } from '../store/actions/properties';
import { delayRetrieveRecords } from '../store/actions/search';

import {
  infoBubbleBottomPadding,
  infoBubbleMouseBuffer,
} from 'helpers/research/map_constants';

export class PropertiesMap extends Component {
  static propTypes = {
    activeProperty: PropTypes.shape({
      id: PropTypes.number,
    }),
    debouncedEnableAutoBoundsSearching: PropTypes.func.isRequired,
    delayRetrieveRecords: PropTypes.func.isRequired,
    disableAutoBoundsSearching: PropTypes.func.isRequired,
    edgeCrm: PropTypes.bool,
    enableAutoBoundsSearching: PropTypes.func.isRequired,
    google: PropTypes.object.isRequired,
    googleMapsApiKey: PropTypes.string.isRequired,
    goToPropertyShow: PropTypes.func.isRequired,
    initialFetchComplete: PropTypes.bool,
    mapMarkerPath: PropTypes.string.isRequired,
    markerAnchor: PropTypes.array.isRequired,
    markerClustererStyles: PropTypes.array.isRequired,
    overlay: PropTypes.bool,
    properties: PropTypes.array.isRequired,
    selectedPropertyIds: PropTypes.array.isRequired,
    setGoogleMapsBounds: PropTypes.func.isRequired,
    setUserBounds: PropTypes.func.isRequired,
    showInfoWindow: PropTypes.bool,
    showMapList: PropTypes.bool,
    toggleMapList: PropTypes.func.isRequired,
    toggleSelectProperty: PropTypes.func.isRequired,
    userBounds: PropTypes.object,
    onHideMapInfoBubble: PropTypes.func.isRequired,
    onSelectProperty: PropTypes.func.isRequired,
  };

  static defaultProps = {
    userBounds: {},
  };

  state = {
    activePropertyId: null,
    enableMapControls: !isMobile(),
    userBounds: {},
  };

  infoBubbleRef = React.createRef();
  mapContainerRef = React.createRef();

  componentDidMount() {
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('resize', this.handleWindowResize);
  }

  componentDidUpdate(prevProps) {
    const { userBounds } = this.props;
    const shouldAdjustMap =
      !prevProps.initialFetchComplete &&
      this.props.initialFetchComplete &&
      this.google &&
      this.map;

    if (shouldAdjustMap) {
      this.adjustMap({ google: this.google }, this.map);
    }

    if (
      this.props.activeProperty &&
      this.props.activeProperty.id !== this.state.activePropertyId
    ) {
      setTimeout(
        () => this.setState({ activePropertyId: this.props.activeProperty.id }),
        50
      );
    }

    const userBoundsFromRefresh =
      isEqual(prevProps.userBounds, userBounds) &&
      this.overlayFromProps == null;
    const userBoundsFromProps =
      !isEqual(userBounds, this.state.userBounds) &&
      !isEqual(prevProps.userBounds, userBounds);

    if (
      (userBoundsFromProps || userBoundsFromRefresh) &&
      userBounds &&
      Object.keys(userBounds).length > 0
    ) {
      // TerraDraw owns rendering the shape; renderBounds returns the bounds to fit the map to.
      this.overlayBounds = this.drawingManager.renderBounds(userBounds);
      this.overlayFromProps = userBounds;

      if (this.overlayBounds) {
        this.map.setOptions({ center: this.overlayBounds.getCenter() });
        this.map.fitBounds(this.overlayBounds);
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
    window.removeEventListener('mousemove', this.handleMouseMove);
    this.drawingManager?.destroy();
  }

  handleMouseMove = (event) => {
    // We don't control the actual rendering of the map bubble, and it turns out that it is often
    // not positioned where you expect when showing it for a new property. Only attempt to hide it
    // if the current active property matches our state, which is set on a slight delay.
    if (
      this.props.activeProperty &&
      this.props.activeProperty.id === this.state.activePropertyId
    ) {
      const { clientX: x, clientY: y } = event;

      const mapBounds = this.mapContainerRef.current.getBoundingClientRect();
      if (
        x < mapBounds.left ||
        x > mapBounds.right ||
        y < mapBounds.top ||
        y > mapBounds.bottom
      ) {
        // Sidebar hovers can show these too - only auto-hide when the map itself
        return;
      }

      const infoBubbleBounds =
        this.infoBubbleRef.current.getBoundingClientRect();
      const xOffset = Math.abs(
        infoBubbleBounds.left + infoBubbleBounds.width / 2 - event.clientX
      );
      const xOffsetRatio = xOffset / (infoBubbleBounds.width / 2);

      if (
        x < infoBubbleBounds.left ||
        x > infoBubbleBounds.right ||
        y < infoBubbleBounds.top ||
        // Need extra space at the bottom for padding/offset and the fact that the mouse starts
        // below the bubble on marker hover. Make the mouseout zone straight lines from that point
        // to each bottom corner of the bubble (which includes extra padding from Google).
        y >
          infoBubbleBounds.bottom +
            infoBubbleBottomPadding +
            infoBubbleMouseBuffer * (1 - xOffsetRatio)
      ) {
        this.props.onHideMapInfoBubble();
      }
    }
  };

  handleMarkerAction = (id) => {
    const { activeProperty } = this.props;

    if (id != null && (activeProperty == null || id !== activeProperty.id)) {
      this.props.onSelectProperty(id);
    }
  };

  handleWindowResize = () => {
    this.props.disableAutoBoundsSearching();
    this.props.debouncedEnableAutoBoundsSearching();

    this.props.delayRetrieveRecords();

    this.setState({ enableMapControls: !isMobile() });
  };

  throttledMarkerAction = debounce(this.handleMarkerAction, 250);

  handleUserBoundsChange = (bounds) => {
    this.setState({ userBounds: bounds });
    this.props.enableAutoBoundsSearching();
    this.props.setUserBounds(bounds);
  };

  // TODO: Handle this through props instead of leaking Google Maps code.
  adjustMap = (mapProps, map) => {
    const { google } = mapProps;
    const { properties } = this.props;

    if (this.map !== map) {
      this.drawingManager = new DrawingManager(google, map, {
        onBoundsChange: this.handleUserBoundsChange,
      });
    }

    this.google = google;
    this.map = map;

    if (properties.length === 0) {
      map.setOptions({
        center: new google.maps.LatLng(39.5, -98.35),
        zoom: 3,
      });
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    properties.forEach((property) => {
      bounds.extend(
        new google.maps.LatLng(property.latitude, property.longitude)
      );
    });

    map.fitBounds(bounds);
  };

  idleEventsHandled = 0;
  handleMapIdle = (map) => {
    // This event is triggered immediately, and again when adjustMap is executed - ignore both
    // TODO: Sometimes it is triggered three times, seemingly only on page refresh - why?
    //       Doesn't hurt anything, but does trigger an extra request, so it would be nice for this to be less jank
    if (this.idleEventsHandled < 2) {
      this.idleEventsHandled += 1;
      return;
    }

    this.props.setGoogleMapsBounds(map.getBounds());
  };

  // Automatic searching may have been unnecessarily disabled if map bubble was shown without changing bounds
  // So, re-enable on a user event that will change them - won't catch arrow key usage, but who does that anyway?
  handleMapDrag = this.props.enableAutoBoundsSearching;
  handleMapZoomChanged = this.props.enableAutoBoundsSearching;

  render() {
    const {
      activeProperty,
      google,
      goToPropertyShow,
      mapMarkerPath,
      markerAnchor,
      markerClustererStyles,
      properties,
      selectedPropertyIds,
      showInfoWindow,
      toggleSelectProperty,
      onHideMapInfoBubble,
    } = this.props;

    if (!google) {
      return <div>Loading...</div>;
    }

    const markers = properties.map(property => ({
      id: property.id,
      icon: { anchor: markerAnchor, url: mapMarkerPath },
      lat: property.latitude,
      lng: property.longitude,
    }));

    const infoWindowContent = (
      <MapInfoBubble
        edgeCrm={this.props.edgeCrm}
        elementRef={this.infoBubbleRef}
        googleMapsApiKey={this.props.googleMapsApiKey}
        goToPropertyShow={goToPropertyShow}
        property={activeProperty}
        selectedPropertyIds={selectedPropertyIds}
        toggleSelectProperty={toggleSelectProperty}
        onCloseWindow={onHideMapInfoBubble}
      />
    );

    const { enableMapControls } = this.state;

    return (
      <React.Fragment>
        {this.renderOverlay()}
        <ClusteredMap
          activeMarkerId={showInfoWindow && activeProperty && activeProperty.id}
          clustererStyles={markerClustererStyles}
          disableInfoWindowHeader={this.props.edgeCrm}
          fullscreenControl={enableMapControls}
          google={google}
          infoWindow={enableMapControls}
          infoWindowContent={activeProperty ? infoWindowContent : null}
          mapContainerRef={this.mapContainerRef}
          mapTypeControl={enableMapControls}
          mapTypeControlOptions={{
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            position: google.maps.ControlPosition.TOP_CENTER,
          }}
          markers={markers}
          maxZoom={18}
          scaleControl={enableMapControls}
          streetViewControl={enableMapControls}
          streetViewControlOptions={{
            position: google.maps.ControlPosition.LEFT_BOTTOM,
          }}
          zoom={14}
          zoomControl={enableMapControls}
          zoomControlOptions={{
            position: google.maps.ControlPosition.TOP_LEFT,
          }}
          onMapDrag={this.handleMapDrag}
          onMapIdle={this.handleMapIdle}
          onMapZoomChanged={this.handleMapZoomChanged}
          onMarkerClick={this.throttledMarkerAction}
          onMarkerMouseover={this.throttledMarkerAction}
          onReady={this.adjustMap}
        />
        {showInfoWindow && (
          <div className="d-block d-md-none">{infoWindowContent}</div>
        )}
      </React.Fragment>
    );
  }

  renderOverlay() {
    const { overlay } = this.props;

    if (overlay) {
      return <div className="loading-overlay"></div>;
    }
  }
}

const mapStateToProps = state => ({
  edgeCrm: state.features.edgeCrm,
  userBounds: state.coordinates.userBounds,
});

const mapDispatchToProps = {
  debouncedEnableAutoBoundsSearching,
  delayRetrieveRecords,
  disableAutoBoundsSearching,
  enableAutoBoundsSearching,
  goToPropertyShow,
  setGoogleMapsBounds,
  setUserBounds,
};

const ConnectedPropertiesMap = connect(
  mapStateToProps,
  mapDispatchToProps
)(PropertiesMap);

const PropertiesMapWithGoogle = (props) => {
  if (typeof window === 'undefined' || !window.google || !window.google.maps) {
    return <div>Loading...</div>;
  }
  return <ConnectedPropertiesMap {...props} google={window.google} />;
};

export default PropertiesMapWithGoogle;
